import React, { useState, useEffect } from 'react';
import { Plus, X, Save, Upload, Image as ImageIcon } from 'lucide-react';
import { appointmentService, ServiceCategory } from '../services/appointmentService';
import { supabaseStorage } from '../services/supabase';
import ImageCropper from './ImageCropper';

interface ServiceCategoryModalProps {
  category?: ServiceCategory;
  onCreated?: () => void;
  onUpdated?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  branch?: 'antofagasta' | 'santiago';
}

export const ServiceCategoryModal: React.FC<ServiceCategoryModalProps> = ({
  category,
  onCreated,
  onUpdated,
  onClose,
  isOpen,
  branch = 'antofagasta'
}) => {
  const [name, setName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);

  // Estados para el cropper
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string | null>(null);

  const isEditMode = !!category;
  const isControlled = isOpen !== undefined;
  const visible = isControlled ? isOpen : internalOpen;

  // Inicializar formulario cuando cambie la categoría
  useEffect(() => {
    if (category) {
      setName(category.name || '');
      setImagePreview(category.image || '');
      setSelectedFile(null);
    } else {
      setName('');
      setImagePreview('');
      setSelectedFile(null);
    }
    setError('');
    setImageError(false);
    setShowCropper(false);
    setTempImageSrc(null);
  }, [category]);

  // Limpiar preview cuando se desmonte
  useEffect(() => {
    return () => {
      if (selectedFile && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [selectedFile, imagePreview]);

  const open = () => {
    setInternalOpen(true);
    setName('');
    setImagePreview('');
    setSelectedFile(null);
    setError('');
    setShowCropper(false);
    setTempImageSrc(null);
  };

  const close = () => {
    if (isControlled && onClose) {
      onClose();
    } else {
      setInternalOpen(false);
    }
    setName('');
    setImagePreview('');
    setSelectedFile(null);
    setError('');
    setUploading(false);
    setLoading(false);
    setShowCropper(false);
    setTempImageSrc(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe superar los 5MB');
      return;
    }

    // Leer archivo para el cropper
    const reader = new FileReader();
    reader.onload = () => {
      setTempImageSrc(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    
    // Limpiar input para permitir seleccionar el mismo archivo de nuevo si se cancela
    e.target.value = '';
  };

  const handleCrop = (blob: Blob) => {
    // Convertir blob a File
    const file = new File([blob], "category-image.jpg", { type: "image/jpeg" });
    
    setSelectedFile(file);
    setError('');
    setImageError(false);

    // Generar preview
    const previewUrl = URL.createObjectURL(blob);
    setImagePreview(previewUrl);
    
    setShowCropper(false);
    setTempImageSrc(null);
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setTempImageSrc(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }

    setLoading(true);
    let imageUrl = category?.image || '';
    let uploadFailed = false;

    try {
      // Si hay un archivo seleccionado, intentar subirlo primero
      if (selectedFile) {
        console.log('[ServiceCategoryModal] Subiendo archivo:', selectedFile.name, selectedFile.size, 'bytes');
        setUploading(true);
        
        try {
          const uploadResult = await supabaseStorage.uploadServiceCategoryImage(
            selectedFile,
            name
          );
          
          console.log('[ServiceCategoryModal] Resultado de subida:', uploadResult);
          setUploading(false);

          if (!uploadResult.success) {
            console.error('[ServiceCategoryModal] Error en subida:', uploadResult.error);
            uploadFailed = true;
            setError(`⚠️ No se pudo subir la imagen: ${uploadResult.error}. ¿Continuar sin imagen?`);
            
            // Dar opción de continuar sin imagen
            if (!window.confirm('No se pudo subir la imagen a Supabase. ¿Deseas guardar la categoría sin imagen?')) {
              setLoading(false);
              return;
            }
            imageUrl = ''; // Guardar sin imagen
          } else {
            imageUrl = uploadResult.publicUrl || '';
            console.log('[ServiceCategoryModal] URL pública obtenida:', imageUrl);
          }
        } catch (uploadError: any) {
          console.error('[ServiceCategoryModal] Excepción en subida:', uploadError);
          setUploading(false);
          uploadFailed = true;
          
          if (!window.confirm(`Error al conectar con Supabase: ${uploadError.message}. ¿Deseas guardar la categoría sin imagen?`)) {
            setLoading(false);
            return;
          }
          imageUrl = ''; // Guardar sin imagen
        }
      }

      // Crear o actualizar la categoría
      // IMPORTANTE: No enviar undefined para image, usar null o omitir el campo
      const payload: any = {
        name: name.trim()
      };
      
      // Solo incluir image si tiene un valor válido o si queremos eliminarla (null)
      if (imageUrl) {
        payload.image = imageUrl;
      } else if (isEditMode && category?.image && !selectedFile) {
        // Si estamos editando y el usuario no seleccionó nueva imagen, mantener la existente
        // No incluir el campo para no sobrescribir
      } else if (selectedFile || imagePreview === '') {
        // Si intentó subir imagen pero falló, o removió la imagen, enviar null
        payload.image = null;
      }

      console.log('[ServiceCategoryModal] Guardando categoría con payload:', payload);

      if (isEditMode && category) {
        if (branch === 'santiago') {
          await appointmentService.updateCategorySantiago(category.id, payload);
        } else {
          await appointmentService.updateCategory(category.id, payload);
        }
        if (onUpdated) onUpdated();
      } else {
        if (branch === 'santiago') {
          await appointmentService.createCategorySantiago(payload);
        } else {
          await appointmentService.createCategory(payload);
        }
        if (onCreated) onCreated();
      }

      console.log('[ServiceCategoryModal] Categoría guardada exitosamente');
      close();
    } catch (err: any) {
      console.error('[ServiceCategoryModal] Error saving service category:', err);
      
      // Determinar si es un error de red o del backend
      let errorMsg = 'Error desconocido';
      if (err?.message === 'Failed to fetch') {
        errorMsg = 'No se puede conectar con el servidor. Verifica que el backend esté corriendo en http://localhost:8000';
      } else if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      
      setError(
        isEditMode 
          ? `Error al actualizar la categoría: ${errorMsg}` 
          : `Error al crear la categoría: ${errorMsg}`
      );
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    // Limpiar el archivo seleccionado
    setSelectedFile(null);
    // Limpiar el preview completamente (no volver a la imagen original)
    setImagePreview('');
    setImageError(false);
    
    // Si estamos editando, actualizar la categoría para eliminar la imagen
    if (isEditMode && category && category.image) {
      if (window.confirm('¿Deseas eliminar la imagen de esta categoría?')) {
        handleUpdateWithoutImage();
      }
    }
  };

  const handleUpdateWithoutImage = async () => {
    if (!isEditMode || !category) return;
    
    setLoading(true);
    try {
      // Enviar null en lugar de string vacío para eliminar la imagen
      if (branch === 'santiago') {
        await appointmentService.updateCategorySantiago(category.id, { image: null as any });
      } else {
        await appointmentService.updateCategory(category.id, { image: null as any });
      }
      setImagePreview('');
      if (onUpdated) onUpdated();
      setError('');
    } catch (err: any) {
      console.error('[ServiceCategoryModal] Error removing image:', err);
      setError('Error al eliminar la imagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showCropper && tempImageSrc && (
        <ImageCropper
          imageSrc={tempImageSrc}
          onCrop={handleCrop}
          onCancel={handleCancelCrop}
          cropShape="rect"
          aspectRatio={1.5} // 3:2 aspect ratio
        />
      )}

      {!isControlled && (
        <button
          onClick={open}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nueva categoría</span>
        </button>
      )}

      {visible && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={close}></div>
          <div className="relative max-w-lg mx-auto mt-24 bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Editar categoría de servicios' : 'Crear categoría de servicios'}
              </h3>
              <button onClick={close} className="text-gray-500 hover:text-gray-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Peluquería, Barbería, etc."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen (opcional)
                </label>
                
                {/* Área de subida de archivo */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {!imagePreview ? (
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-sm text-gray-600 mb-1">
                        Click para seleccionar una imagen
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF hasta 5MB
                      </p>
                    </label>
                  ) : (
                    <div className="space-y-3">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg"
                        onError={() => setImageError(true)}
                      />
                      {imageError && (
                        <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                          <p className="text-sm text-gray-500 ml-2">Imagen no disponible</p>
                        </div>
                      )}
                      <div className="flex space-x-2">
                        <label
                          htmlFor="image-upload"
                          className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-sm text-center"
                        >
                          Cambiar imagen
                        </label>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  Esta imagen se mostrará en la página de agendamiento
                </p>
              </div>

              {uploading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-blue-600">Subiendo imagen...</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={close}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || uploading}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{isEditMode ? 'Actualizando...' : 'Creando...'}</span>
                    </>
                  ) : (
                    <>
                      {isEditMode ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      <span>{isEditMode ? 'Actualizar' : 'Crear'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceCategoryModal;
